import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GenerateEnvironmentDefinition } from '../environment-variables';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { firstValueFrom } from 'rxjs';

export interface OnboardStep {
  title: string;
  subtitle: string;
  img?: string;
}

type ResultSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined;
type ResultIcon = 'pi pi-check-circle' | 'pi pi-times-circle' | 'pi pi-exclamation-circle';

@Component({
  selector: 'app-wizard-modal',
  standalone: false,
  templateUrl: './wizard-modal.component.html',
  styleUrls: ['./wizard-modal.component.scss']
})
export class WizardModalComponent implements OnInit {
    
    // visible = input.required<boolean>();
    @Input() visible!: boolean;

    @Output() public readonly visibleChange = new EventEmitter<boolean>();

    /** Environment flags for feature toggles and labels */
     public readonly env: GenerateEnvironmentDefinition = new GenerateEnvironmentDefinition();
    
     activeIndex = 0;
  
    showWelcome = true;
    
    step = 0;
    
    nif = '';
    
    isSubmitting = false;
    
    requestDone = false;
      
   readonly steps: ReadonlyArray<OnboardStep> = [
      {
        title: '¡Bienvenido a AgroGEST!',
        subtitle: 'Tu herramienta para la gestión del Cuaderno de Campo Digital. Comencemos un breve recorrido por la plataforma.',
        img: 'assets/images/wizard/step1.png',
      },
      {
        title: 'Verificación inicial',
        subtitle: 'Primero, verifiquemos que hayas autorizado el tratamiento de tu información con nosotros',
        img: 'assets/images/wizard/step2.png',
      },
      {
        title: 'Carga los datos de tus explotaciones (REA)',
        subtitle: `¡Con la app autorizada para trabajar con tus datos, importa tus explotaciones en solo 4 clics y 
  comienza a trabajar al instante con la función Importar REA!`,
        img: 'assets/images/wizard/step3.png',
      },
      {
        title: 'Crea tratamientos',
        subtitle: 'Crea tus tratamientos, tanto fitosanitarios como fertilizantes',
        img: 'assets/images/wizard/step4.png',
      },
      {
        title: 'Envía Cuaderno Digital (CUE)',
        subtitle: 'Finalmente, envía tus tratamientos al Ministerio y cumple con toda la normativa sin complicaciones',
        img: 'assets/images/wizard/step5.png',
      },
    ];
  
    private _result: { severity: ResultSeverity; icon: ResultIcon; text: string } | null = null;

    constructor(public http: HttpClient) { void http; }

    get resultSeverity(): ResultSeverity {
        return this._result?.severity ?? 'warn';
    }
    
    get resultIcon(): ResultIcon {
        return this._result?.icon ?? 'pi pi-exclamation-circle';
    }
    
    get resultText(): string {
        return this._result?.text ?? 'Sin resultado';
    }

    get currentStep(): OnboardStep {
        return this.steps[this.step] as OnboardStep;
    }

    // Considered "authorized" only when submitNif() finished and server said success
    get isAuthorized(): boolean {
        return this.requestDone && this._result?.severity === 'success';
    }

    /** Arrow enabled on step > 0 or authorized on step 0 */
    get canContinue(): boolean {
        return this.step === 0 || this.isAuthorized;
    }


    public ngOnInit(): void {
      // Preload all step images to avoid visible swap delay
      for (const s of this.steps) {
        if (s.img != null) {
          const i = new Image();
          
          i.src = s.img;
        }
      }
    }


    onActiveIndexChange(event: number): void {
        this.activeIndex = event;
    }
    
    changeStep(step: string, event: Event): void {
        console.log(step, event);
    }
    
    prev(): void {
      if (this.step > 0) {
        this.step -= 1;
      }
    }
    
    next(): void {
      if (this.step < this.steps.length - 1) {
        this.step += 1;
        return;
      }
      this.showWelcome = false; // fin del wizard
    }
    
    skip(): void {
      
      let user = StorageManager.getUser();

      delete user.password;
      delete user.password_old;
      delete user.password_repeat;

      const headers = new HttpHeaders();
        
      headers.append('Content-Type:', 'application/json');
      headers.append('Request-Method', 'PUT');
      
      user = { ...user, wizard_done: 1 };

      firstValueFrom(
        this.http.put(
          `${this.env.serverUrl}ws/index.php?p1=generic&p2=usuarios&p3=${user.id}`, 
          JSON.stringify(user), 
          { headers: headers }
        )).then(() => {
          this.showWelcome = false;
          this.visibleChange.emit(false);
          StorageManager.saveUser(user);
          this.step = 0;
          return;
        }).catch((e) => {
          console.log('catch en getPosition: ' + e);
      });
    }
    
    checkAuth(): void {
      // this.authSvc.check(this.nif).subscribe(() => this.next());
      this.next();
    }
    
    
    async submitNif(): Promise<void> {
      // Evita envíos dobles
      if (this.isSubmitting || !this.nif) {return;}
    
      this.isSubmitting = true;
      this.requestDone = false;
    
      const url = `${this.env.serverUrl}/ws/index.php?p1=fincas&p2=importREA`;
      const params = new URLSearchParams();
    
      params.set('nif', this.nif.trim());
      params.set('operationType', 'check');
      params.set('autonomousCommunity', this.env.user.codigo_ine ?? this.env.codigoINE);

      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        });
    
        // Intenta leer JSON; si no hay, crea uno mínimo
        let json: unknown;
          
        try { json = await resp.json(); } catch { json = null; }
    
        // Heurística de éxito:
        // - HTTP ok (2xx) o payload con statusCode 200/202
        // - warning si 422, error para el resto
           
        const statusCode = (json as any)?.statusCode as number | undefined;
    
        if (resp.ok || statusCode === HttpStatusCode.Ok || statusCode === HttpStatusCode.Accepted) {
          this._result = {
            severity: 'success',
            icon: 'pi pi-check-circle',
            text: 'La aplicación está autorizada para el tratamiento de tus datos.',
          };
        } else if (statusCode === HttpStatusCode.UnprocessableEntity) {
          const msg = (json as any)?.data?.errores?.[0]?.descripcion ?? 
            'La aplicación no está autorizada para el tratamiento de  tus datos.';
            
          this._result = { severity: 'warn', icon: 'pi pi-exclamation-circle', text: msg };
        } else {
          const msg = (json as any)?.statusMessage ?? 'La aplicación no está autorizada para el tratamiento de  tus datos.';
            
          this._result = { severity: 'danger', icon: 'pi pi-times-circle', text: msg };
        }
      } catch (err) {
        console.error('💥 Error en submitNif => ', err);
        this._result = {
          severity: 'danger',
          icon: 'pi pi-times-circle',
          text: 'Error de red o del servidor',
        };
      } finally {
        this.isSubmitting = false;
        this.requestDone = true; // oculta el botón y muestra el resultado
      }
    }
}
